import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ROOT_TEAM_ID } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';

export interface MainTeamspace {
  /** The teamspace standing as this user's main, or `''` when they are in none at all. */
  id: string;
  /**
   * `true` when the teamspace was CHOSEN for them because they are not in the root teamspace.
   * Surfaces mark only a derived main — a root member's main already renders as "Main Teamspace",
   * so marking it would read "Main Teamspace (main)".
   */
  isDerived: boolean;
}

const NONE: MainTeamspace = { id: '', isDerived: false };

/**
 * Which teamspace is this user's main one? (#2324 D7)
 *
 * Every account has a root teamspace, but not every USER is in it, and a user who is not has no
 * teamspace that reads as "home" — the switcher is a flat list of peers. This resolves one, in
 * three steps, and the answer is DERIVED on every call: nothing is stored, there is no setting,
 * and no migration. If the inputs change the answer changes with them.
 *
 *  1. **Root membership wins.** `me.teams` carries a row with `_id === ROOT_TEAM_ID` exactly when
 *     the user is actually a member (`user.service.ts` adds it only when `authContext.teams['1']`
 *     is present). This is membership, NOT `me.rootRole` — a root role is a capability, and a user
 *     can hold one without being in root, which would name a teamspace they are not in.
 *  2. **Otherwise the Group Security §12 home teamspace**, when a group sets one AND the user is in
 *     it. `homeTeamId` is already what the app lands them on at login (`AuthProvider`), so
 *     disagreeing with it would mean two different answers to "where do I belong". The membership
 *     guard mirrors AuthProvider's: a stale grant can name a teamspace the user has since left.
 *  3. **Otherwise first alphabetically.** An arbitrary rule, deliberately — it only has to be
 *     STABLE, so the same user sees the same teamspace marked every session. `_id` breaks ties so
 *     two identically-named teamspaces can't swap places between renders.
 */
export function useMainTeamspace(): MainTeamspace {
  const teams = useTeamStore((s) => s.teams);
  const homeTeamId = useAuthStore((s) => s.me?.homeTeamId);

  return useMemo(() => {
    if (!teams.length) return NONE;

    if (teams.some((team) => team._id === ROOT_TEAM_ID)) {
      return { id: ROOT_TEAM_ID, isDerived: false };
    }

    if (homeTeamId && teams.some((team) => team._id === homeTeamId)) {
      return { id: homeTeamId, isDerived: true };
    }

    const first = [...teams].sort(
      (a, b) => (a.name || '').localeCompare(b.name || '') || a._id.localeCompare(b._id),
    )[0];

    return first ? { id: first._id, isDerived: true } : NONE;
  }, [teams, homeTeamId]);
}

/**
 * The same marker, for pickers that take a plain STRING label.
 *
 * Which mechanism a surface uses is decided by who owns the row:
 *  - **The surface renders the row itself** (the top-nav switcher, Move-to-teamspace, the share
 *    selector) → render the marker as its OWN node after the name, so a long teamspace name
 *    truncates and the marker survives. A marker that can be ellipsed away is no marker.
 *  - **The surface hands a string to a renderer it does not own** (`FilterPillOption.label` and
 *    friends are typed `string`) → this, which folds the marker into the name.
 *
 * Returns the name unchanged for every teamspace that is not a DERIVED main, so callers can wrap
 * every row without branching.
 */
export function useMainTeamspaceLabeller(): (teamId: string, name: string) => string {
  const { t } = useTranslation();
  const { id, isDerived } = useMainTeamspace();

  return useCallback(
    (teamId: string, name: string) =>
      isDerived && teamId === id ? t('{{name}} (main)', { name }) : name,
    [id, isDerived, t],
  );
}
